import { CroppingGroupVm } from "../../croppingGroup"
import { getDistinctValues, groupBy, IParam, param } from "../../omnia"
import { ReqConfigMode } from "../../types"
import { ConfigVm } from "./config-vm"

/// <reference path="../../croppingGroup.ts" />

export class ConfigGroup extends CroppingGroupVm<ConfigVm> {

    public selected: boolean
    public expanded: boolean
    public keepExpanded: boolean

    public nConfigMode: ReqConfigMode
    public pkConfigMode: ReqConfigMode
    public limeConfigMode: ReqConfigMode
    public cropOfftake: boolean
    public terraMap: boolean

    private configModeFilters: {
        N: IParam[],
        Pk: IParam[],
        Lime: IParam[]
    };

    public constructor(key: string, items: ConfigVm[], private _showLimeOptions: boolean) {

        super(key, items)
        items.forEach(c => c.group = this)

        this.synchronize()

    }

    public synchronize() {
        this.syncSelection(true)
        this.syncModes()
    }

    private syncModes() {

        this.nConfigMode = this.getUniqueItemProperty(c => c.nConfigMode)
        this.pkConfigMode = this.getUniqueItemProperty(c => c.pkConfigMode)
        this.limeConfigMode = this.getUniqueItemProperty(c => c.limeConfigMode)
        this.cropOfftake = this.getUniqueItemProperty(c => !!c.cropOfftake)
        this.terraMap = this.getUniqueItemProperty(c => !!c.terraMap, c => c.showTerraMapOption)

        this.configModeFilters = {
            N: this.getSupportedConfigModes((mode, item) => item.filterNConfigMode(mode.value)),
            Pk: this.getSupportedConfigModes((mode, item) => item.filterPkConfigMode(mode.value)),
            Lime: this.getSupportedConfigModes((mode, item) => item.filterLimeConfigMode(mode.value)),
        }

        this.syncExpandCollapse();

    }

    private syncExpandCollapse() {

        let selected = this.cropping.filter(s => s.selected);

        let keepExpanded = ((selected.length > 0) && (selected.length < this.cropping.length))

        if ((!keepExpanded) && (selected.length > 1)) {

            keepExpanded =
                (groupBy(selected, s => s.nConfigMode).length > 1)
                || (groupBy(selected, s => s.pkConfigMode).length > 1)
                || (groupBy(selected, s => !!s.cropOfftake).length > 1);

            if ((!keepExpanded) && this.showLimeOptions) {
                keepExpanded = groupBy(selected, s => s.limeConfigMode).length > 1;
            }

        }

        this.keepExpanded = keepExpanded;

        if (keepExpanded) {
            this.expanded = true;
        }


    }

    private getSupportedConfigModes(filter: (mode: IParam, item: ConfigVm) => boolean): IParam[] {

        let modes = param.NtrReqConfigMode();

        modes = modes.filter(mode => this.cropping.some(c => filter(mode, c)));

        return modes;

    }

    get showLimeOptions(): boolean {
        return this._showLimeOptions
    }

    set showLimeOptions(value: boolean) {
        this._showLimeOptions = value
        this.updateDefaultLimeMode(value)
        this.synchronize()
    }

    get multipleItems(): boolean {
        return this.cropping.length > 1;
    }

    syncSelection(fromItems?: boolean) {

        if (fromItems) {
            this.selected = this.cropping.some(c => c.selected);
            this.syncExpandCollapse();
        }
        else {
            this.cropping.forEach(c => c.selected = this.selected)
            this.syncModes()
            if (!this.selected) {
                this.expanded = false
            }
        }

    }

    getNConfigModeFilter() {
        return this.getConfigModeFilter("N")
    }

    public syncNConfigMode() {
        if (this.nConfigMode) {
            this.setItemProperty(this.nConfigMode, (item, value) => item.nConfigMode = value);
        }
    }

    getPkConfigModeFilter() {
        return this.getConfigModeFilter("Pk")
    }

    public syncPkConfigMode() {
        if (this.pkConfigMode) {
            this.setItemProperty(this.pkConfigMode, (item, value) => item.pkConfigMode = value);
        }
    }

    getLimeConfigModeFilter() {
        return this.getConfigModeFilter("Lime")
    }

    public syncLimeConfigMode() {
        if (this.limeConfigMode) {
            this.setItemProperty(this.limeConfigMode, (item, value) => item.limeConfigMode = value);
        }
    }

    public syncCropOfftake() {
        this.setItemProperty(this.cropOfftake, (item, value) => item.cropOfftake = value)
        this.synchronize()
    }

    public syncTerraMap() {
        this.setItemProperty(this.terraMap, (item, value) => item.terraMap = item.showTerraMapOption && value)
        this.synchronize()
    }

    private setItemProperty(value, setter: (item: ConfigVm, value: any) => void) {
        this.cropping.filter(c => c.selected).forEach(c => setter(c, value));
        this.syncExpandCollapse();
    }

    private getConfigModeFilter(key: string) {
        let list = this.configModeFilters ? this.configModeFilters[key] : null;
        return (p: IParam) => list ? list.some(i => i.value == p.value) : true;
    }

    private getUniqueItemProperty<T>(valueSelector: (item: ConfigVm) => T, filter?: (item: ConfigVm) => boolean): T {

        filter = filter || (c => true)

        let values = this.cropping.filter(c => c.selected && filter(c)).map(c => valueSelector(c));
        let unique = getDistinctValues(values, v => v);

        if (unique.length == 1) {
            return unique[0];
        }

        return null;

    }

    public toggleExpanded() {
        this.expanded = !this.expanded;
    }

    get showTerraMapOption() {
        return this.cropping.some(c => c.showTerraMapOption)
    }

    private updateDefaultLimeMode(enabled: boolean) {

        const crops = this.cropping.filter(c => c.isNew)

        crops.forEach(c => {

            if (!enabled) {
                c.limeConfigMode = ReqConfigMode.WholeField
            }
            else if (c.hasSoilAnalysisLayer || c.hasTerraMapLayers) {
                c.limeConfigMode = ReqConfigMode.SoilAnalysis
            }

        })

    }
}
